(function () {
  const MOBILE_QUERY = '(max-width: 992px)';
  const stateMap = new WeakMap();

  function getVisibleCount() {
    return window.matchMedia(MOBILE_QUERY).matches ? 4 : 7;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getTrack(section) {
    return section.querySelector('[data-partners-track]');
  }

  function getViewport(section) {
    return section.querySelector('[data-partners-viewport]');
  }

  function getToggle(section) {
    return section.querySelector('[data-partners-toggle]');
  }

  function getLogoCount(section) {
    const total = parseInt(section.dataset.logoCount, 10);
    if (!Number.isNaN(total) && total > 0) return total;

    return section.querySelectorAll('[data-partners-slide]').length;
  }

  function duplicateTrack(section) {
    const track = getTrack(section);
    if (!track || track.dataset.partnersDuplicated === 'true') return;

    const slides = track.querySelectorAll('[data-partners-slide]');
    slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');

      const link = clone.querySelector('a');
      if (link) {
        const media = document.createElement('span');
        media.className = 'collaboration-partners__logo-media';
        media.innerHTML = link.innerHTML;
        link.replaceWith(media);
      }

      clone.querySelectorAll('img').forEach((img) => {
        img.setAttribute('alt', '');
        img.setAttribute('aria-hidden', 'true');
      });

      track.appendChild(clone);
    });

    track.dataset.partnersDuplicated = 'true';
  }

  function needsMarquee(section) {
    return getLogoCount(section) > getVisibleCount();
  }

  function getState(section) {
    if (!stateMap.has(section)) {
      stateMap.set(section, {
        isPlaying: false,
        resumeOnFocusOut: false,
        resumeOnVisible: false,
        bound: false,
      });
    }
    return stateMap.get(section);
  }

  function setMarqueeDuration(section) {
    const track = getTrack(section);
    if (!track) return;

    const durationMs = parseInt(section.dataset.marqueeDuration, 10) || 40000;
    track.style.setProperty('--cp-marquee-duration', `${durationMs}ms`);
  }

  function updateToggleState(section, isPlaying) {
    const toggle = getToggle(section);
    if (!toggle) return;

    const strings = window.accessibilityStrings || {};
    toggle.classList.toggle('collaboration-partners__toggle--paused', !isPlaying);
    toggle.setAttribute('aria-pressed', isPlaying ? 'false' : 'true');
    toggle.setAttribute(
      'aria-label',
      isPlaying
        ? strings.pauseSlideshow || 'Pause slideshow'
        : strings.playSlideshow || 'Play slideshow'
    );
  }

  function play(section, options) {
    const opts = options || {};
    const state = getState(section);
    const track = getTrack(section);
    const viewport = getViewport(section);

    if (!needsMarquee(section) || !track) return;

    track.classList.remove('is-paused');
    state.isPlaying = true;
    if (viewport) viewport.setAttribute('aria-live', 'off');
    updateToggleState(section, true);

    if (!opts.silent) {
      state.resumeOnFocusOut = false;
      state.resumeOnVisible = false;
    }
  }

  function pause(section, options) {
    const opts = options || {};
    const state = getState(section);
    const track = getTrack(section);
    const viewport = getViewport(section);

    if (track) track.classList.add('is-paused');
    state.isPlaying = false;
    if (viewport) viewport.setAttribute('aria-live', 'polite');
    updateToggleState(section, false);

    if (opts.resumeOnFocusOut) state.resumeOnFocusOut = true;
    if (opts.resumeOnVisible) state.resumeOnVisible = true;
  }

  function togglePlayback(section) {
    const state = getState(section);
    if (state.isPlaying) {
      pause(section);
      state.resumeOnFocusOut = false;
      state.resumeOnVisible = false;
    } else {
      play(section);
    }
  }

  function updateStaticState(section) {
    section.classList.toggle('collaboration-partners__carousel--static', !needsMarquee(section));
  }

  function handleResize(section) {
    const state = getState(section);
    updateStaticState(section);

    if (!needsMarquee(section)) {
      const track = getTrack(section);
      if (track) {
        track.classList.add('is-paused');
        track.classList.remove('is-marquee-ready');
      }
      state.isPlaying = false;
      const viewport = getViewport(section);
      if (viewport) viewport.setAttribute('aria-live', 'polite');
      updateToggleState(section, false);
      return;
    }

    duplicateTrack(section);
    setMarqueeDuration(section);

    const track = getTrack(section);
    if (track && !prefersReducedMotion()) {
      track.classList.add('is-marquee-ready');
    }

    if (state.isPlaying) {
      play(section, { silent: true });
    }
  }

  function bindEvents(section) {
    const state = getState(section);
    if (state.bound) return;

    state.bound = true;

    const toggleBtn = getToggle(section);
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => togglePlayback(section));
    }

    section.addEventListener('focusin', (event) => {
      if (!needsMarquee(section)) return;

      const toggleBtn = getToggle(section);
      const focusedToggle =
        toggleBtn && (event.target === toggleBtn || toggleBtn.contains(event.target));

      if (focusedToggle) {
        if (state.isPlaying) play(section);
        return;
      }

      if (state.isPlaying) {
        pause(section, { resumeOnFocusOut: true });
      }
    });

    section.addEventListener('focusout', (event) => {
      if (!needsMarquee(section) || !state.resumeOnFocusOut) return;

      const nextTarget = event.relatedTarget;
      if (nextTarget && section.contains(nextTarget)) return;

      state.resumeOnFocusOut = false;
      play(section, { silent: true });
    });

    section.addEventListener('mouseenter', () => {
      if (!needsMarquee(section) || !state.isPlaying) return;
      pause(section, { resumeOnFocusOut: true });
    });

    section.addEventListener('mouseleave', () => {
      if (!needsMarquee(section) || !state.resumeOnFocusOut) return;
      state.resumeOnFocusOut = false;
      play(section, { silent: true });
    });

    if (!window.__collaborationPartnersResizeBound) {
      window.__collaborationPartnersResizeBound = true;
      window.addEventListener('resize', () => {
        document.querySelectorAll('[data-collaboration-partners]').forEach(handleResize);
      });
    }

    if (!window.__collaborationPartnersVisibilityBound) {
      window.__collaborationPartnersVisibilityBound = true;
      document.addEventListener('visibilitychange', () => {
        document.querySelectorAll('[data-collaboration-partners]').forEach((carousel) => {
          const carouselState = getState(carousel);
          if (document.hidden) {
            if (carouselState.isPlaying) {
              pause(carousel, { resumeOnVisible: true });
            }
          } else if (carouselState.resumeOnVisible) {
            carouselState.resumeOnVisible = false;
            play(carousel, { silent: true });
          }
        });
      });
    }
  }

  function initSection(section) {
    if (!section || section.dataset.partnersInitialized === 'true') return;

    section.dataset.partnersInitialized = 'true';
    bindEvents(section);
    updateStaticState(section);
    setMarqueeDuration(section);

    if (!needsMarquee(section)) {
      pause(section);
      return;
    }

    duplicateTrack(section);

    const track = getTrack(section);
    if (!prefersReducedMotion()) {
      if (track) track.classList.add('is-marquee-ready');
      play(section);
    } else {
      pause(section);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-collaboration-partners]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-collaboration-partners]');
    if (!section) return;

    const track = getTrack(section);
    if (track) {
      track.classList.remove('is-marquee-ready');
      track.dataset.partnersDuplicated = 'false';
      track.querySelectorAll('[data-partners-slide][aria-hidden="true"]').forEach((clone) => {
        clone.remove();
      });
    }

    section.dataset.partnersInitialized = 'false';
    initSection(section);
  });
})();
